// Motor de cálculo da Projeção Financeira.
// Port fiel de Bia/src/lib/domain/projection.ts para JavaScript puro.
// Regras de domínio (planilhas rentabilidade1.xlsx / projecaorentabilidade2.xlsx):
// - receitaBruta, taxaHeppi, receitaLiquida e saida são arredondados individualmente por mês.
// - Os acumuladores (saldoAcumulado, saldoSemRendimento, rendimentoAcumulado) NUNCA são
//   arredondados dentro do loop — apenas no objeto de retorno. Arredondar mid-loop
//   quebra a precisão em relação à planilha de referência.
// - O IR incide UMA ÚNICA VEZ no final sobre o rendimentoBrutoTotal, não por mês.

import { addMonths, endOfMonth, format, startOfMonth } from 'date-fns'

export function roundCurrency (value) {
  return Math.round((value + Number.EPSILON) * 100) / 100
}

function parseInputDate (value) {
  if (value && value.includes('T')) return new Date(value)
  return new Date(`${value}T12:00:00.000Z`)
}

export function calculateProjection (input) {
  const monthlyRate = input.taxaRendimentoMensal / 100
  const saidasPorMes = new Map()
  for (const saida of input.saidas || []) {
    saidasPorMes.set(saida.mes, (saidasPorMes.get(saida.mes) || 0) + saida.valor)
  }

  let saldoAcumulado = 0
  let saldoSemRendimento = 0
  let rendimentoAcumulado = 0

  return Array.from({ length: input.horizonteMeses }, (_, monthIndex) => {
    const monthDate = startOfMonth(addMonths(parseInputDate(input.dataInicio), monthIndex))
    const monthKey = format(monthDate, 'yyyy-MM')
    const receitaBruta = roundCurrency(input.quantidadeAlunos * input.mensalidade)
    const taxaHeppi = roundCurrency(receitaBruta * (input.taxaHeppi / 100))
    const receitaLiquida = roundCurrency(receitaBruta - taxaHeppi)
    const saida = roundCurrency(saidasPorMes.get(monthKey) || 0)
    const baseParaRendimento = saldoAcumulado + receitaLiquida - saida
    const rendimento = baseParaRendimento * monthlyRate
    saldoAcumulado = baseParaRendimento + rendimento
    saldoSemRendimento += receitaLiquida - saida
    rendimentoAcumulado += rendimento

    return {
      monthIndex,
      month: monthDate.toISOString(),
      periodLabel: format(monthDate, 'MMM/yy'),
      quantidadeAlunos: input.quantidadeAlunos,
      receitaBruta,
      taxaHeppi,
      receitaLiquida,
      saida,
      rendimento: roundCurrency(rendimento),
      saldoSemRendimento: roundCurrency(saldoSemRendimento),
      rendimentoAcumulado: roundCurrency(rendimentoAcumulado),
      saldoAcumulado: roundCurrency(saldoAcumulado)
    }
  })
}

export function calculateKpis (results, input) {
  const receitaTotal = roundCurrency(
    results.reduce((sum, item) => sum + item.receitaBruta, 0)
  )
  const taxaHeppiTotal = roundCurrency(
    results.reduce((sum, item) => sum + item.taxaHeppi, 0)
  )
  const receitaLiquidaTotal = roundCurrency(
    results.reduce((sum, item) => sum + item.receitaLiquida, 0)
  )
  const saidasTotal = roundCurrency(
    results.reduce((sum, item) => sum + item.saida, 0)
  )
  const rendimentoBrutoTotal = roundCurrency(
    results.reduce((sum, item) => sum + item.rendimento, 0)
  )
  const impostoTotal = roundCurrency(rendimentoBrutoTotal * (input.aliquotaIR / 100))
  const lucroProjetado = roundCurrency(rendimentoBrutoTotal - impostoTotal)
  const last = results.length ? results[results.length - 1] : null
  const saldoFinal = roundCurrency(last ? last.saldoAcumulado : 0)
  const capitalInvestido = last ? last.saldoSemRendimento : 0
  const roi =
    capitalInvestido === 0 ? 0 : roundCurrency((lucroProjetado / capitalInvestido) * 100)
  const rentabilidadeMedia =
    results.length === 0
      ? 0
      : roundCurrency(
        (results.reduce((sum, item) => {
          const base = item.saldoAcumulado - item.rendimento
          return base === 0 ? sum : sum + item.rendimento / base
        }, 0) /
          results.length) *
          100
      )

  return {
    receitaTotal,
    receitaLiquidaTotal,
    saidasTotal,
    rendimentoBrutoTotal,
    impostoTotal,
    lucroProjetado,
    roi,
    rentabilidadeMedia,
    saldoFinal,
    taxaHeppiTotal
  }
}

export function groupBySemester (results) {
  const groups = new Map()

  for (const result of results) {
    const date = new Date(result.month)
    const year = date.getFullYear()
    const semester = date.getMonth() < 6 ? 1 : 2
    const key = `${year}-S${semester}`
    const current = groups.get(key) || []
    current.push(result)
    groups.set(key, current)
  }

  return Array.from(groups.entries()).map(([semesterKey, items]) => {
    const start = startOfMonth(new Date(items[0].month))
    const end = endOfMonth(new Date(items[items.length - 1].month))
    const year = start.getFullYear()
    const semesterLabel = start.getMonth() < 6 ? `Jan-Jun ${year}` : `Jul-Dez ${year}`
    const last = items[items.length - 1]

    return {
      semesterKey,
      semesterLabel,
      startMonth: start.toISOString(),
      endMonth: end.toISOString(),
      receitaBruta: roundCurrency(items.reduce((sum, item) => sum + item.receitaBruta, 0)),
      taxaHeppi: roundCurrency(items.reduce((sum, item) => sum + item.taxaHeppi, 0)),
      receitaLiquida: roundCurrency(
        items.reduce((sum, item) => sum + item.receitaLiquida, 0)
      ),
      saidas: roundCurrency(items.reduce((sum, item) => sum + item.saida, 0)),
      rendimento: roundCurrency(items.reduce((sum, item) => sum + item.rendimento, 0)),
      saldoSemRendimento: last.saldoSemRendimento,
      rendimentoAcumulado: last.rendimentoAcumulado,
      saldoFinal: last.saldoAcumulado
    }
  })
}

function scenarioLabel (key) {
  if (key === 'optimistic') return 'Cenário otimista'
  if (key === 'pessimistic') return 'Cenário pessimista'
  return 'Cenário base'
}

export function buildScenario (input, delta, key) {
  const quantityFactor = Math.max(1, Math.round(input.quantidadeAlunos * (1 + delta)))
  const scenarioInput =
    key === 'base'
      ? input
      : {
        ...input,
        quantidadeAlunos: quantityFactor
      }

  const results = calculateProjection(scenarioInput)
  const summaries = groupBySemester(results)
  const kpis = calculateKpis(results, scenarioInput)

  return {
    key,
    label: scenarioLabel(key),
    delta,
    input: scenarioInput,
    results,
    summaries,
    kpis
  }
}

export function buildAllScenarios (input) {
  return [
    buildScenario(input, 0, 'base'),
    buildScenario(input, 0.2, 'optimistic'),
    buildScenario(input, -0.2, 'pessimistic')
  ]
}
